import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';

import NotificationsIcon from '@mui/icons-material/Notifications';
import DeleteIcon from '@mui/icons-material/Clear';
import ReadAllIcon from '@mui/icons-material/ChecklistRtl';
import DeleteAllIcon from '@mui/icons-material/LayersClear';
import { Badge, Box, IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material';

import {
  useLazyDeleteAllNotificationsQuery,
  useLazyDeleteNotificationByIdQuery,
  useLazyGetNotificationsQuery,
  useLazyGetNotificationsUnreadCountQuery,
  useLazyReadAllNotificationQuery,
  useLazyReadNotificationByIdQuery,
} from '../../api/controllers/notifications';
import { UTCToDateTime } from '../../utils/dates';
import { notificationSelector } from '../../store/selectors';
import { useAppActions, useAppSelector } from '../../utils/hooks';
import { GetNotificationsArg } from '../../api/controllers/notifications/type';
import { notificationsAction } from '../../store/notifications/notifications';

export default function Notifications() {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [getNotifications, { data: notificationList }] = useLazyGetNotificationsQuery();
  const [getUnreadCount, { data: unreadCount }] = useLazyGetNotificationsUnreadCountQuery();
  const [deleteAll] = useLazyDeleteAllNotificationsQuery();
  const [deleteById] = useLazyDeleteNotificationByIdQuery();
  const [readAll] = useLazyReadAllNotificationQuery();
  const [readById] = useLazyReadNotificationByIdQuery();
  const { setNotifications, addNewNotifications, updateNotificationList } =
    useAppActions(notificationsAction);
  const notificationListStore = useAppSelector(notificationSelector);
  const [notificationArg, setNotificationArg] = useState<GetNotificationsArg>({
    limit: 5,
    offset: 0,
  });

  useEffect(() => {
    getNotifications(notificationArg);
    getUnreadCount();
  }, []);

  useEffect(() => {
    if (notificationList) {
      updateNotificationList(notificationList?.notifications);
    }
  }, [notificationList]);

  const onScrollList = (event: any) => {
    const scrollBottom =
      event.target.scrollTop + event.target.offsetHeight == event.target.scrollHeight;
    if (scrollBottom && notificationListStore.length >= notificationArg.offset) {
      setNotificationArg((prevState) => {
        return {
          ...prevState,
          offset: notificationListStore.length,
        };
      });
    }
  };

  useEffect(() => {
    getNotifications(notificationArg);
  }, [notificationArg.offset]);

  const onDeleteNotificationById = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: string
  ) => {
    e.preventDefault();
    e.stopPropagation();

    deleteById(id).then(() => {
      setNotifications(notificationListStore.filter((it) => it.id !== id));
      enqueueSnackbar(t('ui.notification.success-delete'), { variant: 'info' });
    });
  };

  const onReadNotificationById = (id: string) => {
    readById(id).then(() => {
      const copyObject = notificationListStore.map((it) => ({
        ...it,
        is_read: it.is_read,
      }));
      const readNotification = copyObject?.find((it) => it.id === id);

      if (readNotification) {
        readNotification.is_read = true;
        setNotifications(copyObject);
      }
    });
  };

  const onDeleteAllNotification = () => {
    deleteAll().then(() => {
      setNotifications([]);
      enqueueSnackbar(t('ui.notification.success-all-delete'), { variant: 'info' });
    });
  };

  const onReadAllNotification = () => {
    readAll().then(() => {
      const copyObject = notificationListStore.map((it) => ({
        ...it,
        is_read: true,
      }));
      setNotifications(copyObject);
      enqueueSnackbar(t('ui.notification.success-all-read'), { variant: 'info' });
    });
  };

  const notifivationListMapped = useMemo(() => {
    getUnreadCount();

    return notificationListStore?.map((it) => {
      return (
        <Box
          key={it.id}
          sx={{
            background: it.is_read ? '#ffffff' : '#1976d224',
            width: '100%',
            minHeight: '50px',
            p: '8px',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            cursor: 'pointer',
          }}
          onClick={() => onReadNotificationById(it.id)}
        >
          <Stack direction="column" spacing={1}>
            <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
              <Typography component="span" variant="body2" color="inherit" lineHeight={'34px'}>
                {UTCToDateTime(it.row_start)}
              </Typography>
              <IconButton
                color="inherit"
                size="small"
                onClick={(e) => onDeleteNotificationById(e, it.id)}
              >
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            </Stack>
            <Typography component="span" variant="subtitle1" color="inherit" sx={{ flexGrow: 1 }}>
              {it.message}
            </Typography>
          </Stack>
        </Box>
      );
    });
  }, [notificationListStore]);

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen}>
        <Badge badgeContent={unreadCount?.count} color="secondary">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        sx={{
          borderRadius: '20px',
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={handleClose}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box
          sx={{
            maxHeight: '450px',
            background: '#ffffff',
            width: '300px',
          }}
        >
          <Box
            sx={{
              background: '#1976d2',
              width: '100%',
              height: '50px',
              p: '8px',
            }}
          >
            <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
              <Typography component="h1" variant="h6" color="#fff" noWrap sx={{ flexGrow: 1 }}>
                {t('ui.notification.title')}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Read all">
                  <IconButton color="default" size="small" onClick={onReadAllNotification}>
                    <ReadAllIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete all">
                  <IconButton color="default" size="small" onClick={onDeleteAllNotification}>
                    <DeleteAllIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>
          <Box
            sx={{
              width: '100%',
              overflowX: 'hidden',
              overflowY: 'auto',
              maxHeight: '400px',
            }}
            onScroll={onScrollList}
          >
            {notificationListStore.length > 0 ? (
              notifivationListMapped
            ) : (
              <Box
                sx={{
                  width: '100%',
                }}
              >
                <Typography
                  component="h1"
                  variant="h5"
                  noWrap
                  sx={{
                    flexGrow: 1,
                    p: '10px',
                    justifyContent: 'center',
                    display: 'flex',
                  }}
                >
                  {t('ui.notification.no-notification')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Popover>
    </>
  );
}
